import { Box, IconButton, Typography } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DescriptionIcon from '@mui/icons-material/Description';
import { formatBase64ContentLengthToFileSize } from '../../../utils/formatFileSize';
import { useDispatch } from 'react-redux';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { showSnackbar } from '../../../api/snackbarSlice';
import { apiClient } from '../../../utils/axios';

const AttachmentViewer = ({ attachments, docId, isPurchase = false, isCreditNote = false }) => {
  const dispatch = useDispatch();
  const { snackbarMessages } = useTranslatedMessages();
  const pdfMessages = snackbarMessages.document_pdf ?? {};

  const getDownloadUrl = (attachmentId) => {
    if (!docId || !attachmentId) {
      return null;
    }

    if (isCreditNote) {
      return `/${isPurchase ? 'purchase' : 'sales'}-credit-notes/${docId}/attachments/${attachmentId}/download`;
    }

    return `/${isPurchase ? 'purchase' : 'sales'}-invoices/${docId}/attachments/${attachmentId}/download`;
  };

  const parseFileNameFromHeader = (contentDisposition, fallbackFileName) => {
    if (!contentDisposition) {
      return fallbackFileName;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
    if (quotedMatch?.[1]) {
      return quotedMatch[1];
    }

    const unquotedMatch = contentDisposition.match(/filename=([^;]+)/i);
    if (unquotedMatch?.[1]) {
      return unquotedMatch[1].trim();
    }

    return fallbackFileName;
  };

  const base64ToBlob = (base64Content, mimeType) => {
    const byteCharacters = atob(base64Content);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  };

  const fetchAttachmentBlob = async (file) => {
    if (file?.file_content) {
      return {
        blob: base64ToBlob(file.file_content, file.file_type),
        fileName: file.file_name,
      };
    }

    const downloadUrl = getDownloadUrl(file?.id);
    if (!downloadUrl) {
      throw new Error('Attachment download context is missing');
    }

    const response = await apiClient.get(downloadUrl, {
      responseType: 'blob',
      headers: {
        Accept: file?.file_type ?? 'application/octet-stream',
      },
    });

    const headerFileName = parseFileNameFromHeader(response.headers?.['content-disposition'], file.file_name);
    const xFileName = response.headers?.['x-suggested-filename'];
    const fileName = xFileName ?? headerFileName ?? file.file_name ?? 'attachment';

    return {
      blob: new Blob([response.data], { type: file.file_type ?? response.data?.type ?? 'application/octet-stream' }),
      fileName,
    };
  };

  const downloadFile = async (file) => {
    try {
      const { blob, fileName } = await fetchAttachmentBlob(file);
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      window.URL.revokeObjectURL(url);
      dispatch(showSnackbar({ message: pdfMessages.document_download_success, severity: 'success' }));
    } catch (error) {
      dispatch(showSnackbar({ message: pdfMessages.document_download_failed, severity: 'error' }));
      console.error(error);
    }
  };

  const printFile = async (file) => {
    try {
      const { blob, fileName } = await fetchAttachmentBlob(file);
      const printWindow = window.open(URL.createObjectURL(blob), '_blank');
      if (!printWindow) {
        dispatch(showSnackbar({ message: pdfMessages.print_window_unavailable, severity: 'error' }));
        return;
      }

      printWindow.onload = () => {
        printWindow.document.filename = fileName;
        printWindow.focus();
        printWindow.print();
      };
      dispatch(showSnackbar({ message: pdfMessages.document_print_success, severity: 'success' }));
    } catch (error) {
      dispatch(showSnackbar({ message: pdfMessages.document_print_failed, severity: 'error' }));
      console.error(error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
      <Box sx={{ my: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          Supporting Documents
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', border: '1px solid #ccc', borderRadius: 1 }}>
        {attachments.map((file, index) => (
          <Box key={index} sx={{ borderBottom: '1px solid #ccc', px: 1, py: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon fontSize="small" color="black" />
                <Typography variant="body2">{file.file_name}</Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="caption">
                  {file.file_content ? formatBase64ContentLengthToFileSize(file.file_content) : ''}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => downloadFile(file)}
                  data-testid="download-pdf-button"
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  }}
                >
                  <SaveAltIcon fontSize="small" color="black" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => printFile(file)}
                  data-testid="print-pdf-button"
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  }}
                >
                  <PrintIcon fontSize="small" color="black" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AttachmentViewer;
